<template>
    <div class="s-event-location" :class="className">
        <h2 v-if="l10n.title" class="title">
            {{ l10n.title }}
        </h2>
        <div class="content">
            <div id="map" class="map">
                <template v-if="!isLoadedGoogleMaps">
                    <div v-loading="true" el-loading-size="48" :el-loading-text="l10n.loadingText" />
                </template>
            </div>

            <div v-if="showFeatures" class="features">
                <div v-if="l10n.location" class="feature">
                    <div class="caption">
                        {{ l10n.location.title }}
                    </div>
                    <div class="value location-title">
                        {{ l10n.location.value }}
                    </div>
                </div>
                <div v-if="l10n.address" class="feature">
                    <div class="caption">
                        {{ l10n.address.title }}
                    </div>
                    <div class="value">
                        {{ l10n.address.value }}
                    </div>
                </div>
                <div v-if="l10n.website" class="feature">
                    <div class="caption">
                        {{ l10n.website.title }}
                    </div>
                    <div class="value">
                        <a class="event-link" :href="l10n.website.value" target="_blank">
                            {{ trimLink(l10n.website.value) }}
                        </a>
                    </div>
                </div>
                <div v-if="l10n.phone" class="feature">
                    <div class="caption">
                        {{ l10n.phone.title }}
                    </div>
                    <div class="value">
                        <a class="event-link" :href="`tel:${l10n.phone.value}`">{{ l10n.phone.value }}</a>
                    </div>
                </div>
            </div>

            <div v-if="photos.length" class="photos">
                <template v-if="showSlider">
                    <a-slider :options="sliderOptions">
                        <a-slide v-for="(photo, i) in photos" :key="photo">
                            <div v-modal="`${i}-${photo}`" class="slider-photo">
                                <a-picture
                                    :link="photo"
                                    position="center"
                                    :is-shadow="false"
                                    persist-original
                                />
                                <div class="zoom-in">
                                    <a-glyph name="zoom-in" class="icon" />
                                </div>
                            </div>
                        </a-slide>
                    </a-slider>
                    <a-modal
                        v-for="(photo, i) in photos"
                        :id="`${i}-${photo}`"
                        :key="photo"
                        is-overlay-close
                    >
                        <a-picture
                            :link="photo"
                            position="center"
                            :is-shadow="false"
                            persist-original
                        />
                    </a-modal>
                </template>
                <div v-else class="photos-row">
                    <a-media
                        v-for="photo in photos"
                        :key="photo"
                        :link="photo"
                        :enlarged-image="photo"
                        type="screenshot"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loading from '@uikit/ui-kit/packages/loading/src/directive.js';
import AGlyph from '@core/components/glyph/glyph.vue';
import AMedia from '@core/components/media/media.vue';
import AModal from '@core/components/modal/modal.vue';
import APicture from '@core/components/picture/picture.vue';
import ASlide from '@core/components/slider/slide.vue';
import ASlider from '@core/components/slider/slider.vue';
import Modal from '@core/directives/modal.js';
import breakpoint from '@core/mixins/breakpoint.js';
import googleMaps from '@core/mixins/google-maps.js';
import l10n from '@core/mixins/l10n.js';

export default {
    name: 'SEventLocation',
    directives: {
        Loading, Modal,

    },
    components: {
        ASlider, ASlide, AModal, AGlyph, APicture, AMedia,
    },
    mixins: [googleMaps, breakpoint, l10n],
    props: {
        /**
         * Slice title
         */
        title: {
            type: String,
            default: null,
        },
        /**
         * location title
         */
        location: {
            type: Object,
            default: null,
        },
        /**
         * Location website
         */
        website: {
            type: Object,
            default: null,
        },
        /**
         * Location phone
         */
        phone: {
            type: Object,
            default: null,
        },
        /**
         * Location address
         */
        address: {
            type: Object,
            default: null,
        },
        /**
         * Map marker
         */
        marker: {
            type: Object,
            required: true,
        },
        /**
         * Loading text
         */
        loadingText: {
            type: String,
            default: null,
        },
        /**
         * Venue photos
         */
        photos: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        sliderOptions() {
            const sliderPerView = this.isMobile ? 'auto' : 2.5;
            return {
                spaceBetween: 16,
                slidesPerView: sliderPerView,
                observer: true,
                observeParents: true,
                watchOverflow: false,
                showNavigation: false,
                showCounter: false,
                useAutoplay: false,
                useLoop: false,
                useBarsPagination: false,
            };
        },

        showFeatures() {
            return this.l10n.location || this.l10n.address || this.l10n.website || this.l10n.phone;
        },

        className() {
            const i = this.photos.length;
            return {
                [`layout-photos-${i}`]: i > 0,
            };
        },

        showSlider() {
            if (this.currentBreakpoint === 'mobile' && this.photos.length === 2) return true;
            if (this.isMobile && this.photos.length === 3) return true;
            if (!this.isDesktop && this.photos.length > 3) return true;
            return false;
        },
    },
    methods: {
        async addMap() {
            const { Map } = await window.google.maps.importLibrary('maps');
            const position = new window.google.maps.LatLng(this.marker.lat, this.marker.lng);
            this.map = new Map(document.getElementById('map'), {
                center: position,
                mapId: 'mapEvent',
                mapTypeControl: true,
                scrollwheel: false,
                streetViewControl: false,
                zoom: 17,
            });
        },
        async addMarkers() {
            const { AdvancedMarkerElement } = await window.google.maps.importLibrary('marker');
            const position = new window.google.maps.LatLng(this.marker.lat, this.marker.lng);
            const createMarker = () => {
                const img = document.createElement('img');
                img.src = '/public/assets/images/marker.svg';
                return img;
            };
            const marker = new AdvancedMarkerElement({
                content: createMarker(),
                map: this.map,
                position,
            });
            marker.zIndex = 0;
            this.isLoadedGoogleMaps = true;
        },
        trimLink(link) {
            return link.replace(/^[^/]*?\/\//i, '');
        },
    },
};
</script>

<style lang="postcss" scoped>
.s-event-location {
    &:deep(.media-wrapper) {
        width: 100%;
    }

    .slider-photo {
        position: relative;
        border-radius: 4px;
        border: 1px solid var(--av-brand-secondary-light, rgba(64, 139, 234, .3));
        overflow: hidden;
        transition: opacity .2s ease-in-out, box-shadow .2s ease-in-out;
        will-change: opacity;

        &:hover {
            opacity: 0.6;

            .a-picture {
                transform: scale(1.02);
            }

            .zoom-in {
                background: var(--av-button-main-hover);
                box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
            }
        }
    }

    &:deep(.a-slide .a-picture__img) {
        width: 100%;
    }

    &.layout-photos-1 {
        .features {
            order: 3;
        }
        .photos {
            order: 2;
        }

        @media (--viewport-desktop) {
            .features {
                @mixin colls 12;
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
            }
            .photos {
                @mixin colls 6;
            }
            .map {
                @mixin colls 6;
                min-height: 332px;
            }
            .content {
                flex-wrap: wrap;
            }
            .value.location-title {
                @mixin paragraph;
                font-weight: 500;
            }
        }

        .map,
        &:deep(.a-picture__img) {
            @media (--viewport-desktop) {
                height: 332px;
            }
            @media (--viewport-desktop-wide) {
                height: 360px;
            }
            @media (--viewport-desktop-large) {
                height: 400px;
            }
        }
    }

    &.layout-photos-2 {
        @media (--viewport-mobile-wide) {
            &:deep(.media-wrapper) {
                @mixin colls 6;
            }
        }

        @media (--viewport-desktop) {
            .content {
                flex-wrap: wrap;
            }
        }

        &:deep(.a-picture__img) {
            @media (--viewport-mobile-wide) {
                height: 190px;
            }
            @media (--viewport-desktop) {
                height: 282px;
            }
            @media (--viewport-desktop-wide) {
                height: 338px;
            }
            @media (--viewport-desktop-large) {
                height: 364px;
            }
        }
    }

    &.layout-photos-3 {
        @media (--viewport-tablet) {
            &:deep(.media-wrapper) {
                @mixin colls 4;
            }
        }

        @media (--viewport-desktop) {
            .content {
                flex-wrap: wrap;
            }
        }

        &:deep(.a-picture__img) {
            @media (--viewport-mobile-wide) {
                height: 190px;
            }
            @media (--viewport-desktop) {
                height: 170px;
            }
            @media (--viewport-desktop-wide) {
                height: 188px;
            }
            @media (--viewport-desktop-large) {
                height: 204px;
            }
        }
    }

    &.layout-photos-4 {
        @media (--viewport-desktop) {
            .content {
                flex-wrap: wrap;
            }
            &:deep(.media-wrapper) {
                @mixin colls 3;
            }
        }

        &:deep(.a-picture__img) {
            @media (--viewport-mobile-wide) {
                height: 214px;
            }
            @media (--viewport-desktop) {
                height: 218px;
            }
            @media (--viewport-desktop-wide) {
                height: 252px;
            }
        }
    }

    &:deep(.a-picture__img) {
        height: 240px;
        object-fit: cover;

        @media (--viewport-mobile-wide) {
            height: 384px;
        }
    }
}

.title {
    margin-bottom: 40px;
}

.photos-row {
    display: flex;
    gap: 16px;
}

.content {
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media (--viewport-desktop) {
        flex-direction: row;
    }
}

.features {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    column-gap: 16px;
    background-color: var(--av-brand-lightest);
    padding: 16px;
    border-radius: 8px;

    @media (--viewport-mobile-wide) {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }

    @media (--viewport-desktop) {
        @mixin colls 4;
        display: flex;
    }
}

.feature {
    @mixin paragraph;
}

.caption {
    @mixin paragraph-accent;
    color: var(--av-nav-primary);
}

.value {
    color: var(--av-fixed-secondary);

    &.location-title {
        @mixin lead-accent;

        @media (--viewport-desktop) {
            @mixin title-accent;
        }
    }
}

.map {
    height: 400px;
    background: var(--av-fixed-invisible);

    @media (--viewport-desktop) {
        @mixin colls 8;
        min-height: 400px;
        height: auto;
        aspect-ratio: 2.14;
    }
    &:deep(.el-loading-parent--relative) {
        width: 100%;
        height: 100%;
        .caption {
            opacity: 0;
        }
    }
}

.photos {
    width: 100%;
}

.zoom-in {
    display: flex;
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: var(--av-fixed-lighter);
    border-radius: 4px;
    padding: 8px;
    transition: 0.2s all;

    &:active {
        background: var(--av-button-main-active);
        box-shadow: 0 10px 20px 0 var(--av-fixed-lightest);
    }
    .icon {
        fill: var(--av-inversed-primary);
    }
}
</style>
